import React from 'react';
import { Box, Button, Chip, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, ptBR } from '@mui/x-data-grid';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { criaCookieToken, getConfigAmbienteDominio } from 'services/authentication/auth';
import SkeletonTabela, { SkeletonTabelaInicial } from '../SkeletonTabela/SkeletonTabela';
import './TabelaResultados.css';

const OPCOES_TAMANHO_PAGINA = [5, 10, 25, 50];

const formataDataHora = dataStr => {
  if (!dataStr) return '';
  const data = new Date(dataStr);
  if (Number.isNaN(data.getTime())) return dataStr;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const montarItensPaginacao = (paginaAtual, totalPaginas) => {
  if (totalPaginas <= 0) return [];

  if (totalPaginas <= 7) {
    return Array.from({ length: totalPaginas }, (_, indice) => indice + 1);
  }

  if (paginaAtual <= 4) {
    return [1, 2, 3, 4, '...', totalPaginas];
  }

  if (paginaAtual >= totalPaginas - 3) {
    return [1, '...', totalPaginas - 3, totalPaginas - 2, totalPaginas - 1, totalPaginas];
  }

  return [1, '...', paginaAtual - 1, paginaAtual, paginaAtual + 1, '...', totalPaginas];
};

const PaginacaoCustom = ({ totalRegistros, paginationModel, onPaginationModelChange }) => {
  const totalPaginas = Math.ceil(totalRegistros / paginationModel.pageSize);
  const paginaAtual = totalPaginas > 0 ? Math.min(paginationModel.page + 1, totalPaginas) : 1;
  const inicio = totalRegistros === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
  const fim =
    totalRegistros === 0
      ? 0
      : Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalRegistros);
  const itensPaginacao = montarItensPaginacao(paginaAtual, totalPaginas);

  const alterarPagina = numeroPagina => {
    onPaginationModelChange({ page: numeroPagina - 1, pageSize: paginationModel.pageSize });
  };

  const irParaAnterior = () => {
    if (paginationModel.page <= 0) return;
    onPaginationModelChange({ page: paginationModel.page - 1, pageSize: paginationModel.pageSize });
  };

  const irParaProximo = () => {
    if (totalPaginas === 0 || paginationModel.page >= totalPaginas - 1) return;
    onPaginationModelChange({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize });
  };

  const handleTamanhoPagina = event => {
    const novoTamanho = Number(event.target.value);
    onPaginationModelChange({ page: 0, pageSize: novoTamanho });
  };

  return (
    <Box className="paginacao-custom">
      <Typography className="paginacao-custom-resumo">
        Mostrando {inicio} de {fim}, entre {totalRegistros} resultados.
      </Typography>

      <Box className="paginacao-custom-controles">
        <Button
          className="paginacao-navegacao"
          onClick={irParaAnterior}
          disabled={paginationModel.page <= 0 || totalPaginas === 0}
          startIcon={<KeyboardDoubleArrowLeftRoundedIcon />}
        >
          Anterior
        </Button>

        <Box className="paginacao-paginas">
          {itensPaginacao.map((item, indice) =>
            item === '...' ? (
              <Typography key={`reticencias-${indice}`} className="paginacao-reticencias">
                ...
              </Typography>
            ) : (
              <button
                key={`pagina-${item}`}
                type="button"
                className={`paginacao-pagina ${item === paginaAtual ? 'ativo' : ''}`}
                onClick={() => alterarPagina(item)}
              >
                {item}
              </button>
            )
          )}
        </Box>

        <Button
          className="paginacao-navegacao"
          onClick={irParaProximo}
          disabled={totalPaginas === 0 || paginationModel.page >= totalPaginas - 1}
          endIcon={<KeyboardDoubleArrowRightRoundedIcon />}
        >
          Próximo
        </Button>

        <Select
          size="small"
          value={paginationModel.pageSize}
          onChange={handleTamanhoPagina}
          className="paginacao-seletor"
          displayEmpty
        >
          {OPCOES_TAMANHO_PAGINA.map(opcao => (
            <MenuItem key={opcao} value={opcao}>
              {opcao}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

const OverlaySemResultados = () => (
  <Box className="overlay-sem-resultados">
    <Typography className="overlay-sem-resultados-titulo">Nenhuma remessa encontrada</Typography>
    <Typography className="overlay-sem-resultados-descricao">
      Quando a consulta retornar registros, eles serão exibidos aqui.
    </Typography>
  </Box>
);

const handleAlterar = codRemessa => {
  if (!criaCookieToken()) {
    window.location.replace('/force-logout');
    return;
  }

  const dominioUrl = getConfigAmbienteDominio();

  let url = `${process.env.REACT_APP_ISEVEN_BASE_URL}/AuthKeyCloack.php?url=/mod/eTes/altremessabanco.php?CODREMESSA=${codRemessa}`;
  url = url.replace('TENANT', dominioUrl);
  setTimeout(() => {
    window.open(url, '_blank');
  }, 200);
};

const TabelaResultados = ({
  resultados,
  carregando,
  totalRegistros,
  paginationModel,
  sortModel,
  onPaginationModelChange,
  onSortModelChange
}) => {
  const columns = [
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      renderCell: params => (
        <Button
          variant="outlined"
          size="small"
          className="btn-alterar-remessa"
          startIcon={<EditIcon />}
          onClick={() => handleAlterar(params.row.cod_remessa)}
        >
          Alterar
        </Button>
      )
    },
    {
      field: 'numero_remessa_banco',
      headerName: 'Remessa Banco',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      renderCell: params => <span className="valor-negrito">{params.value}</span>
    },
    {
      field: 'nome_banco',
      headerName: 'Banco',
      width: 180,
      renderCell: params => (
        <Tooltip title={params.value} arrow>
          <span>
            {params.row.cod_banco} - {params.value}
          </span>
        </Tooltip>
      )
    },
    {
      field: 'agencia',
      headerName: 'Agência',
      width: 120,
      renderCell: params => {
        const digito = params.row.digito_agencia;
        return digito ? `${params.value}-${digito}` : params.value;
      }
    },
    {
      field: 'conta_corrente',
      headerName: 'C/C',
      width: 140,
      renderCell: params => {
        const digito = params.row.digito_conta_corrente;
        return digito ? `${params.value}-${digito}` : params.value;
      }
    },
    {
      field: 'titular',
      headerName: 'Titular',
      flex: 1,
      minWidth: 180,
      renderCell: params => (
        <Tooltip title={params.value} arrow>
          <span className="texto-truncado">{params.value}</span>
        </Tooltip>
      )
    },
    {
      field: 'tipo_remessa_descricao',
      headerName: 'Tipo',
      width: 150,
      renderCell: params => {
        const valorTipoRemessa = params.value || '-';
        return (
          <Tooltip title={valorTipoRemessa} arrow>
            <span className="chip-tipo-remessa-wrapper">
              <Chip
                label={valorTipoRemessa}
                variant="outlined"
                size="small"
                color={params.row.tipo_remessa === 'E' ? 'primary' : 'success'}
                className="chip-tipo-remessa"
              />
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: 'valor_remessa',
      headerName: 'Valor',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: params => (
        <span
          className={`valor-negrito ${params.value && params.value !== '0,00' ? 'valor-positivo' : ''}`}
        >
          R$ {params.value}
        </span>
      )
    },
    {
      field: 'data_inclusao',
      headerName: 'Inclusão',
      width: 170,
      renderCell: params => (
        <div className="celula-inclusao">
          <span>{params.row.data_inclusao ? formataDataHora(params.row.data_inclusao) : ''}</span>
          <span>{params.row.usuario_alteracao}</span>
        </div>
      )
    }
  ];

  return (
    <Box className="tabela-resultados-container">
      <Box className="tabela-resultados-resumo">
        <Typography variant="body2" className="tabela-resultados-contagem">
          <strong>{totalRegistros}</strong> registro(s) encontrado(s)
        </Typography>
        <Typography variant="caption" className="tabela-resultados-nota">
          Registros dos últimos 30 dias são exibidos ao aplicar a pesquisa.
        </Typography>
      </Box>

      <Box className="tabela-resultados-grid">
        {carregando && resultados.length === 0 ? (
          <SkeletonTabelaInicial />
        ) : (
          <DataGrid
            rows={resultados}
            columns={columns}
            loading={carregando}
            className="datagrid-remessas"
            getRowId={row => row.cod_remessa}
            hideFooter
            paginationMode="server"
            pageSizeOptions={OPCOES_TAMANHO_PAGINA}
            rowHeight={72}
            columnHeaderHeight={56}
            disableRowSelectionOnClick
            disableColumnMenu
            slots={{
              loadingOverlay: SkeletonTabela,
              noRowsOverlay: OverlaySemResultados,
              noResultsOverlay: OverlaySemResultados
            }}
            localeText={{
              ...ptBR.components.MuiDataGrid.defaultProps.localeText
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            rowCount={totalRegistros}
            getRowClassName={params =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'linha-par' : 'linha-impar'
            }
          />
        )}
      </Box>

      {!(carregando && resultados.length === 0) && (
        <PaginacaoCustom
          totalRegistros={totalRegistros}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
        />
      )}
    </Box>
  );
};

export default TabelaResultados;
